import React from "react";
import { fmtCurrency, fmtNumber } from "../../shared/format";
import PanelTakeaway from "../../shared/PanelTakeaway";
import { KPI_ACCENT_BORDERS, KPI_ICON_COLORS, KPI_ICONS } from "../../shared/kpiTokens";
import { RefreshingOverlay } from "../../shared/PanelShell";

// THE MONEY STRIP — Addendum 1 §2, Tier 2: FOUR cells, asymmetric.
//
// The V6 three-cell strip (Recoverable / Bookings / After hours) is replaced. The
// backend no longer returns `confirmed: 0` — the agent has been completing
// reservations in Campspot all along, and fn_get_conversation_bookings now returns
// four separate, honest numbers instead of one that understated. The strip renders
// them side by side and NEVER sums them: an owner asks two different questions —
// "did the agent sell something?" (agent_booked) and "did it help me sell
// something?" (agent_influenced) — and merging 46 and 19 into a "65 bookings"
// figure is exactly the overstatement removed from this product.
//
//   1 (lead) Reservations         → agent_booked.count / .revenue
//                                    drill fn_drill('reservation','paid')
//   2        Booked after talking  → agent_influenced.count / .revenue
//                                    drill fn_drill('outcome','reservation_made')
//   3        In progress           → in_progress.count / .value (built, not paid —
//                                    recoverable) drill fn_drill('reservation','unpaid')
//   4 Phone  After hours           → agent_value.after_hours.calls_answered
//                                    drill fn_drill('after_hours', null)
//   4 Chat   Booking enquiries     → existing_booking_enquiries.count (callers about
//                                    a booking they already had — service, never
//                                    revenue) drill fn_drill('outcome','reservation_assisted')
//
// CARD ANATOMY (user ruling 2026-09-04): the four cells use the house KPI card
// anatomy — a coloured top rule, an oversized watermark icon bled off the top-right
// corner, and the house label/value type — reusing KpiCard's shared tokens
// (KPI_ACCENT_BORDERS / KPI_ICON_COLORS / KPI_ICONS), the same way search-console's
// MetricTile borrows them. The four cells are equal width. The accent colour is
// DECORATIVE ONLY — it distinguishes the cards and carries no good/bad judgement
// (the same rule MetricTile documents); the label and each backend `means` string
// carry the meaning, so nothing depends on hue in greyscale or print.
//
// Each cell renders its backend `means` string verbatim; the per-modality `note`
// renders once, verbatim, beneath the row. Recoverable left the strip — it belongs
// in the follow-up region, and still surfaces through KeyFindings via
// agent_value.recoverable.
//
// after_hours still comes from fn_get_agent_value (modality-scoped, Phone-only);
// every booking number comes from fn_get_conversation_bookings. The old
// agent_value.bookings object is gone and nothing here reads it.

// The KPI card chrome: accent top rule + corner watermark + house label. The value
// area is the caller's children (a drillable number, its "worth" suffix and the
// means line), rendered above the watermark via `relative z-10`.
// `accent` overrides the variant's top-border colour. Prefer a NAMED Tailwind colour
// (e.g. `border-t-violet-500`): an arbitrary value like `border-t-[#32B1FF]` collides
// with the arbitrary `border-t-[3px]` width on the same element and Tailwind drops
// it, leaving a grey top edge — which is why the primary token can't be a hex here.
const KpiShell = ({ testId, variant = "primary", accent, icon = "chart", label, children }) => (
  <div
    data-testid={testId}
    className={`relative bg-white dark:bg-gray-900 rounded-xl shadow-sm border-x border-b border-gray-200 dark:border-gray-800 border-t-[3px] ${
      accent ?? KPI_ACCENT_BORDERS[variant] ?? KPI_ACCENT_BORDERS.primary
    } p-4 min-h-[120px] flex flex-col overflow-hidden`}
  >
    <div className="absolute inset-0 overflow-hidden rounded-xl pointer-events-none">
      <div className={`absolute -top-4 -right-4 ${KPI_ICON_COLORS[variant] ?? KPI_ICON_COLORS.primary}`}>
        <svg className="w-20 h-20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden="true">
          {KPI_ICONS[icon] ?? KPI_ICONS.chart}
        </svg>
      </div>
    </div>
    {/* The house card label — gi-card-label, the same scoped larger step StatCard
        uses (a cell label sits in the same relationship to its figure). */}
    <p className="relative z-10 gi-card-label text-gray-500 dark:text-gray-400">
      {label}
    </p>
    {children}
  </div>
);

// A drillable region: a button when a drill object and handler are both present
// (every drill on this page opens the same DrillOver), a plain wrapper otherwise —
// a figure with no drill target must not pretend to be clickable.
const Drillable = ({ drill, onDrill, title, className = "", children }) => {
  if (!onDrill || !drill) return <div className={className}>{children}</div>;
  return (
    <button
      type="button"
      onClick={() => onDrill(drill, title)}
      className={`text-left rounded-md min-h-[44px] px-1 -mx-1 hover:bg-gray-900/5 dark:hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] ${className}`}
    >
      {children}
    </button>
  );
};

// The house KPI value: a big extrabold number in the tabular figure face.
// The big number takes its card's accent colour so it matches the top-border rule
// (user ruling 2026-09-05) — same hue family as KPI_ACCENT_BORDERS per variant.
const NUMBER_ACCENT = {
  primary: "text-[#32B1FF]",
  success: "text-teal-500 dark:text-teal-400",
  muted: "text-sky-500 dark:text-sky-400",
  subtle: "text-indigo-500 dark:text-indigo-400",
};
// `override` lets a cell whose accent border isn't a variant token (see KpiShell's
// `accent`) colour its number to match — keeping the number and the top border the
// same hue (user ruling 2026-09-05).
const numberClass = (variant, override) =>
  `text-2xl font-extrabold tabular-nums ${override || NUMBER_ACCENT[variant] || NUMBER_ACCENT.primary}`;

// One count-and-value cell (Reservations / Booked after talking / In progress).
// The count and what it is worth are ONE fact — a reservation count without its
// revenue is half the statement. A zero VALUE is omitted ("$0.00" reads as a
// measured amount); a zero COUNT renders, because "0" beside the note is the honest
// state for a property whose agent has not booked yet.
const ValueCell = ({ testId, label, variant, accent, numberColor, icon, count, value, drill, onDrill, title, means }) => (
  <KpiShell testId={testId} variant={variant} accent={accent} icon={icon} label={label}>
    <Drillable drill={drill} onDrill={onDrill} title={title} className="relative z-10 mt-1.5 flex items-baseline gap-2 flex-wrap">
      <span className={numberClass(variant, numberColor)}>{fmtNumber(count)}</span>
      {value != null && Number(value) !== 0 && (
        <span className="text-sm text-gray-600 dark:text-gray-400">worth {fmtCurrency(value)}</span>
      )}
    </Drillable>
    {means && <p className="relative z-10 mt-auto pt-1.5 text-sm text-gray-500 dark:text-gray-400 leading-snug">{means}</p>}
  </KpiShell>
);

// TEMPORARY STOPGAP — Markup #5. The Booking-enquiries `means` sentence is written
// by the backend (existing_booking_enquiries.means) and printed verbatim; on Chat it
// reads "Callers asking about a booking they already had…", but chat visitors do not
// call. The real fix is backend (make that sentence modality-aware); until it lands
// this swaps caller→visitor on the Chat page only. REMOVE this helper and its call
// once the backend sentence is corrected — leaving it would double-fix a corrected
// string. This is the one place the render-only surface edits a backend sentence, and
// it is deliberately narrow: the enquiries cell only, and only the caller/visitor noun.
const chatifyMeans = (text) =>
  typeof text === "string"
    ? text
        .replace(/\bCallers\b/g, "Visitors")
        .replace(/\bcallers\b/g, "visitors")
        .replace(/\bCaller\b/g, "Visitor")
        .replace(/\bcaller\b/g, "visitor")
    : text;

const MoneyStrip = ({ say, panelKeyPrefix, bookings, agentValue, showAfterHours, onDrill, loading = false }) => {
  const b = bookings || {};
  const afterHours = agentValue?.after_hours || {};

  const agentBooked = b.agent_booked || {};
  const agentInfluenced = b.agent_influenced || {};
  const inProgress = b.in_progress || {};
  const enquiries = b.existing_booking_enquiries || {};

  // Chat visitors chat, they do not talk — the influenced cell reads "Booked after
  // chatting" on Chat, "Booked after talking" on Phone (Markup #4). Label only; the
  // count, drill and means are unchanged.
  const isChat = panelKeyPrefix === "chat";
  const influencedLabel = isChat ? "Booked after chatting" : "Booked after talking";

  const hasReservations = agentBooked.count != null;
  const hasInfluenced = agentInfluenced.count != null;
  const hasInProgress = inProgress.count != null;
  // The fourth cell is After hours on Phone, Booking enquiries on Chat — the page
  // chooses via showAfterHours. Voice-only AND non-empty for after-hours; a
  // structural absence on chat never renders an empty after-hours cell.
  const hasAfterHours = !!showAfterHours && afterHours.calls_answered != null;
  const hasEnquiries = !showAfterHours && enquiries.count != null;

  if (!hasReservations && !hasInfluenced && !hasInProgress && !hasAfterHours && !hasEnquiries) return null;

  return (
    <div className="relative space-y-4">
      {loading && <RefreshingOverlay />}
      {/* The say-map sentence: the backend now writes it from the four numbers, so
          there is no frontend change here — it renders whatever string it is given.
          The shared PanelTakeaway (not a fork) with `mb-2` for the strip's tighter
          spacing. */}
      <PanelTakeaway panelKey={`${panelKeyPrefix}_bookings`} say={say} className="mb-2" />

      {/* FOUR KPI cards with an emphasised lead (Markup #3): Reservations (the first
          cell) takes 1.55fr, the other three 1fr each, so the lead reads as the
          headline the reviewer asked for without dropping the Addendum-1 fourth data
          cell. Below 1600px the strip is a 2-up grid (all cells 50%), so the last two
          cells (In progress / After hours) wrap to a second row — the four-in-a-row
          lead emphasis only applies at ≥1600px. (User-requested 1600px cut point, off
          the standard ladder.) The four numbers still must never read as one combined
          total; the separate cards, labels and per-cell `means` strings carry that,
          and the accent colours are decorative. */}
      <div
        data-testid={`${panelKeyPrefix}-money-strip`}
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 min-[1600px]:grid-cols-[1.55fr_1fr_1fr_1fr]"
      >
        {hasReservations && (
          // Reservations drills reservation/PAID intentionally (§2.3 — the 46 paid
          // rows), NOT the payload's embedded agent_booked.drill hint of {id:null},
          // which would drill all 61 paid+unpaid rows and mismatch the 46 shown.
          // Keep the literal 'paid' — do not "fix" it to the payload hint.
          <ValueCell
            testId={`${panelKeyPrefix}-cell-reservations`}
            label="Reservations"
            variant="primary"
            accent="border-t-violet-500 dark:border-t-violet-400"
            numberColor="text-violet-500 dark:text-violet-400"
            icon="dollar"
            count={agentBooked.count}
            value={agentBooked.revenue}
            drill={{ what: "reservation", id: "paid" }}
            onDrill={onDrill}
            title="Reservations"
            means={agentBooked.means}
          />
        )}

        {hasInfluenced && (
          <ValueCell
            testId={`${panelKeyPrefix}-cell-influenced`}
            label={influencedLabel}
            variant="success"
            icon="conversions"
            count={agentInfluenced.count}
            value={agentInfluenced.revenue}
            drill={{ what: "outcome", id: "reservation_made" }}
            onDrill={onDrill}
            title={influencedLabel}
            means={agentInfluenced.means}
          />
        )}

        {hasInProgress && (
          <ValueCell
            testId={`${panelKeyPrefix}-cell-in-progress`}
            label="In progress"
            variant="muted"
            icon="booking"
            count={inProgress.count}
            value={inProgress.value}
            drill={{ what: "reservation", id: "unpaid" }}
            onDrill={onDrill}
            title="In progress"
            means={inProgress.means}
          />
        )}

        {hasAfterHours && (
          <KpiShell testId={`${panelKeyPrefix}-cell-after-hours`} variant="subtle" icon="phone" label="After hours">
            <Drillable drill={{ what: "after_hours", id: null }} onDrill={onDrill} title="After hours" className={`relative z-10 mt-1.5 ${numberClass("subtle")}`}>
              {fmtNumber(afterHours.calls_answered)}
            </Drillable>
            <p className="relative z-10 text-sm text-gray-600 dark:text-gray-400 mt-1">calls answered out of hours</p>
            {afterHours.counterfactual && (
              <p className="relative z-10 text-sm text-gray-500 dark:text-gray-400 mt-auto pt-1.5 leading-snug">{afterHours.counterfactual}</p>
            )}
          </KpiShell>
        )}

        {hasEnquiries && (
          <KpiShell testId={`${panelKeyPrefix}-cell-booking-enquiries`} variant="subtle" icon="users" label="Booking enquiries">
            <Drillable drill={{ what: "outcome", id: "reservation_assisted" }} onDrill={onDrill} title="Booking enquiries" className={`relative z-10 mt-1.5 ${numberClass("subtle")}`}>
              {fmtNumber(enquiries.count)}
            </Drillable>
            {/* Callers about a booking they already had. The agent answers the
                question; it cannot change or cancel a reservation — the means string
                says so, verbatim, and it is service, never revenue. */}
            {enquiries.means && (
              <p className="relative z-10 text-sm text-gray-500 dark:text-gray-400 mt-auto pt-1.5 leading-snug">{chatifyMeans(enquiries.means)}</p>
            )}
          </KpiShell>
        )}
      </div>

      {/* The note, per-modality, rendered verbatim beneath the row. Still true about
          front-desk bookings the agent cannot see. */}
      {b.note && (
        <p data-testid={`${panelKeyPrefix}-bookings-note`} className="gi-body text-gray-600 dark:text-gray-400">
          {b.note}
        </p>
      )}
    </div>
  );
};

export default MoneyStrip;
